import React, { useState } from 'react';
import {
  Box,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Heading,
  Text,
  VStack,
  Alert,
  AlertIcon,
  useToast
} from '@chakra-ui/react';
import CreditLimitManager from '../components/CreditLimitManager';
import TranscriptionLogs from '../components/TranscriptionLogs';
import { getSupabase } from '../components/supabaseClient';
import { useEffect } from 'react';
import { normalizeRole } from '../config/roles';

const AdminDashboard = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    checkAdminStatus();
  }, []);

  const checkAdminStatus = async () => {
    try {
      const supabase = await getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      const { data: userData, error } = await supabase
        .from('users')
        .select('role')
        .eq('id', session.user.id)
        .single();

      if (error) throw error;

      if (normalizeRole(userData?.role) === 'admin') {
        setIsAdmin(true);
      } else {
        setIsAdmin(false);
        toast({
          title: 'Access Denied',
          description: 'You do not have admin privileges to access this page.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
      toast({
        title: 'Error',
        description: 'Failed to verify admin status.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box textAlign="center" py={8}>
        <Text>Loading...</Text>
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box p={6}>
        <Alert status="error">
          <AlertIcon />
          Access denied. Admin privileges required to view this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading size="lg" mb={2}>Admin Dashboard</Heading>
          <Text color="gray.600">
            Manage credit limits and view transcription logs for all users.
          </Text>
        </Box>

        {/* Lazy so Transcription Logs only loads once its tab is opened
            (Chakra otherwise mounts every panel, and its reads ran behind
            Credit Limits on every visit); kept mounted so switching back and
            forth keeps each tab's state. */}
        <Tabs variant="enclosed" isLazy lazyBehavior="keepMounted">
          <TabList>
            <Tab>Credit Limits</Tab>
            <Tab>Transcription Logs</Tab>
          </TabList>

          <TabPanels>
            <TabPanel>
              <CreditLimitManager />
            </TabPanel>
            
            <TabPanel>
              <TranscriptionLogs />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
    </Box>
  );
};

export default AdminDashboard;
